import { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useI18n } from '@/i18n';
import {
  Button,
  Card,
  Icon,
  Input,
  Label,
  Textarea,
} from '@/components/ui';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  useDataSource,
  useCreateDataSource,
  useUpdateDataSource,
  useBindDataSource,
  useOAuthInitiate,
  useOAuthCallback,
} from '../hooks/use-datasources';
import { useCollections } from '@/hooks/use-knowledge';
import type { ProviderType, FilterConfig, SyncConfig, OAuthProvider } from '../types';
import { PROVIDER_LABELS, PROVIDER_OAUTH_MAP, OAUTH_DISPLAY_NAMES } from '../types';

// =============================================================================
// Default configs
// =============================================================================

const DEFAULT_FILTER_CONFIG: FilterConfig = {
  include_extensions: ['.pdf', '.docx', '.md', '.txt', '.xlsx', '.pptx'],
  exclude_patterns: [],
  max_file_size_bytes: 104857600,
  max_depth: 10,
};

const DEFAULT_SYNC_CONFIG: SyncConfig = {
  interval_minutes: 60,
  max_retries: 3,
};

// =============================================================================
// OAuth Connect Button
// =============================================================================

interface OAuthConnectProps {
  provider: OAuthProvider;
  displayName: string;
  connected: boolean;
  onConnected: (credentialToken: string) => void;
  t: (key: string) => string;
}

function OAuthConnectButton({ provider, displayName, connected, onConnected, t }: OAuthConnectProps) {
  const initiate = useOAuthInitiate();
  const callback = useOAuthCallback();
  const [connecting, setConnecting] = useState(false);
  const popupRef = useRef<Window | null>(null);
  const stateRef = useRef<string>('');

  // Listen for the popup callback message
  const handleMessage = useCallback(
    async (event: MessageEvent) => {
      if (event.data?.type !== 'oauth-callback') return;

      const { code, state, error } = event.data;
      if (error) {
        toast.error(t('datasources.oauth.consentDenied'));
        setConnecting(false);
        return;
      }

      // Verify the state matches
      if (state !== stateRef.current) {
        toast.error(t('datasources.oauth.stateMismatch'));
        setConnecting(false);
        return;
      }

      try {
        const result = await callback.mutateAsync({ code, state });
        onConnected(result.credential_token);
        toast.success(t('datasources.oauth.connected'));
      } catch {
        toast.error(t('datasources.oauth.callbackFailed'));
      } finally {
        setConnecting(false);
      }
    },
    [callback, onConnected, t],
  );

  useEffect(() => {
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleMessage]);

  const handleConnect = async () => {
    if (connected) return;
    setConnecting(true);

    try {
      const { authorization_url, state } = await initiate.mutateAsync({
        provider,
      });
      stateRef.current = state;

      // Open popup
      const w = 600;
      const h = 700;
      const left = window.screenX + (window.outerWidth - w) / 2;
      const top = window.screenY + (window.outerHeight - h) / 2;
      popupRef.current = window.open(
        authorization_url,
        'oauth-popup',
        `width=${w},height=${h},left=${left},top=${top},popup=yes`,
      );

      if (!popupRef.current) {
        toast.error(t('datasources.oauth.initFailed'));
        setConnecting(false);
      }
    } catch {
      toast.error(t('datasources.oauth.initFailed'));
      setConnecting(false);
    }
  };

  if (connected) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 dark:bg-green-900/20 dark:border-green-800 px-4 py-3">
        <Icon name="check-circle" size={18} className="text-green-600" />
        <span className="text-sm font-medium text-green-700 dark:text-green-300">
          {t('datasources.oauth.accountConnected')}
        </span>
        <span className="text-xs text-green-600 dark:text-green-400 ml-auto">
          {displayName}
        </span>
      </div>
    );
  }

  return (
    <Button
      type="button"
      variant="outline"
      onClick={handleConnect}
      disabled={connecting}
      className="w-full justify-center gap-2 py-6 text-base"
    >
      {connecting ? (
        <Icon name="loader" size={18} className="animate-spin" />
      ) : (
        <Icon name="external-link" size={18} />
      )}
      {connecting
        ? t('datasources.oauth.connecting')
        : t('datasources.oauth.connectWith').replace('{provider}', displayName)}
    </Button>
  );
}

// =============================================================================
// Provider Config Forms
// =============================================================================

interface ProviderConfigProps {
  config: Record<string, unknown>;
  onChange: (config: Record<string, unknown>) => void;
  credential: Record<string, unknown>;
  onCredentialChange: (cred: Record<string, unknown>) => void;
  t: (key: string) => string;
  oauthProvider?: OAuthProvider;
  oauthConnected: boolean;
  onOAuthConnected: (credentialToken: string) => void;
}

function GitConfigForm({
  config, onChange, credential, onCredentialChange, t, oauthProvider, oauthConnected, onOAuthConnected,
}: ProviderConfigProps) {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const displayName = oauthProvider ? OAUTH_DISPLAY_NAMES[oauthProvider] : '';

  return (
    <div className="space-y-4">
      <div>
        <Label>{t('datasources.form.repoUrl')}</Label>
        <Input
          placeholder="https://github.com/org/repo.git"
          value={(config.repo_url as string) || ''}
          onChange={(e) => onChange({ ...config, repo_url: e.target.value })}
        />
        <p className="text-xs text-gray-500 mt-1">{t('datasources.form.repoUrlHint')}</p>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>{t('datasources.form.branch')}</Label>
          <Input
            placeholder="main"
            value={(config.branch as string) || 'main'}
            onChange={(e) => onChange({ ...config, branch: e.target.value })}
          />
        </div>
        <div>
          <Label>{t('datasources.form.path')}</Label>
          <Input
            placeholder="docs/"
            value={(config.path as string) || ''}
            onChange={(e) => onChange({ ...config, path: e.target.value })}
          />
          <p className="text-xs text-gray-500 mt-1">{t('datasources.form.pathHint')}</p>
        </div>
      </div>
      {/* Auth: OAuth default + Advanced manual fallback */}
      <div className="border-t pt-4 mt-4">
        <h4 className="text-sm font-medium mb-3">{t('datasources.form.authentication')}</h4>
        {oauthProvider && !showAdvanced && (
          <div className="space-y-3">
            <OAuthConnectButton
              provider={oauthProvider}
              displayName={displayName}
              connected={oauthConnected}
              onConnected={onOAuthConnected}
              t={t}
            />
          </div>
        )}
        {showAdvanced && (
          <div>
            <Label>{t('datasources.form.token')}</Label>
            <Input
              type="password"
              placeholder={t('datasources.form.tokenPlaceholder')}
              value={(credential.token as string) || ''}
              onChange={(e) => onCredentialChange({ ...credential, token: e.target.value })}
            />
            <p className="text-xs text-gray-500 mt-1">{t('datasources.form.tokenHint')}</p>
          </div>
        )}
        {oauthProvider && !oauthConnected && (
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="mt-3 text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 underline"
          >
            {showAdvanced
              ? t('datasources.oauth.backToOAuth')
              : t('datasources.oauth.advanced')}
          </button>
        )}
      </div>
    </div>
  );
}

function S3ConfigForm({ config, onChange, credential, onCredentialChange, t }: ProviderConfigProps) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>{t('datasources.form.bucket')}</Label>
          <Input
            placeholder="my-docs-bucket"
            value={(config.bucket as string) || ''}
            onChange={(e) => onChange({ ...config, bucket: e.target.value })}
          />
        </div>
        <div>
          <Label>{t('datasources.form.region')}</Label>
          <Input
            placeholder="us-east-1"
            value={(config.region as string) || 'us-east-1'}
            onChange={(e) => onChange({ ...config, region: e.target.value })}
          />
        </div>
      </div>
      <div>
        <Label>{t('datasources.form.prefix')}</Label>
        <Input
          placeholder="documents/"
          value={(config.prefix as string) || ''}
          onChange={(e) => onChange({ ...config, prefix: e.target.value })}
        />
        <p className="text-xs text-gray-500 mt-1">{t('datasources.form.prefixHint')}</p>
      </div>
      <div>
        <Label>{t('datasources.form.endpointUrl')}</Label>
        <Input
          placeholder="https://s3.custom.endpoint.com"
          value={(config.endpoint_url as string) || ''}
          onChange={(e) => onChange({ ...config, endpoint_url: e.target.value || null })}
        />
        <p className="text-xs text-gray-500 mt-1">{t('datasources.form.endpointUrlHint')}</p>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>{t('datasources.form.accessKeyId')}</Label>
          <Input
            type="password"
            placeholder="AKIA..."
            value={(credential.access_key_id as string) || ''}
            onChange={(e) => onCredentialChange({ ...credential, access_key_id: e.target.value })}
          />
        </div>
        <div>
          <Label>{t('datasources.form.secretAccessKey')}</Label>
          <Input
            type="password"
            placeholder="••••"
            value={(credential.secret_access_key as string) || ''}
            onChange={(e) => onCredentialChange({ ...credential, secret_access_key: e.target.value })}
          />
        </div>
      </div>
    </div>
  );
}

function SharePointConfigForm({
  config, onChange, t, oauthProvider, oauthConnected, onOAuthConnected, credential, onCredentialChange,
}: ProviderConfigProps) {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const displayName = oauthProvider ? OAUTH_DISPLAY_NAMES[oauthProvider] : '';

  return (
    <div className="space-y-4">
      <div>
        <Label>{t('datasources.form.siteUrl')}</Label>
        <Input
          placeholder="https://contoso.sharepoint.com/sites/docs"
          value={(config.site_url as string) || ''}
          onChange={(e) => onChange({ ...config, site_url: e.target.value })}
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>{t('datasources.form.library')}</Label>
          <Input
            placeholder="Documents"
            value={(config.library as string) || 'Documents'}
            onChange={(e) => onChange({ ...config, library: e.target.value })}
          />
        </div>
        <div>
          <Label>{t('datasources.form.folder')}</Label>
          <Input
            placeholder="/"
            value={(config.folder as string) || '/'}
            onChange={(e) => onChange({ ...config, folder: e.target.value })}
          />
        </div>
      </div>
      {/* Auth: OAuth default + Advanced manual fallback */}
      <div className="border-t pt-4 mt-4">
        <h4 className="text-sm font-medium mb-3">{t('datasources.form.authentication')}</h4>
        {oauthProvider && !showAdvanced && (
          <div className="space-y-3">
            <OAuthConnectButton
              provider={oauthProvider}
              displayName={displayName}
              connected={oauthConnected}
              onConnected={onOAuthConnected}
              t={t}
            />
          </div>
        )}
        {showAdvanced && (
          <div className="space-y-4">
            <div>
              <Label>{t('datasources.form.tenantIdAzure')}</Label>
              <Input
                placeholder="00000000-0000-0000-0000-000000000000"
                value={(credential.tenant_id as string) || ''}
                onChange={(e) => onCredentialChange({ ...credential, tenant_id: e.target.value })}
              />
            </div>
            <div>
              <Label>{t('datasources.form.clientId')}</Label>
              <Input
                placeholder="00000000-0000-0000-0000-000000000000"
                value={(credential.client_id as string) || ''}
                onChange={(e) => onCredentialChange({ ...credential, client_id: e.target.value })}
              />
            </div>
            <div>
              <Label>{t('datasources.form.clientSecret')}</Label>
              <Input
                type="password"
                placeholder="••••"
                value={(credential.client_secret as string) || ''}
                onChange={(e) => onCredentialChange({ ...credential, client_secret: e.target.value })}
              />
            </div>
          </div>
        )}
        {oauthProvider && !oauthConnected && (
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="mt-3 text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 underline"
          >
            {showAdvanced
              ? t('datasources.oauth.backToOAuth')
              : t('datasources.oauth.advanced')}
          </button>
        )}
      </div>
    </div>
  );
}

const PROVIDER_FORMS: Record<string, React.ComponentType<ProviderConfigProps>> = {
  git: GitConfigForm,
  s3: S3ConfigForm,
  sharepoint: SharePointConfigForm,
};

// =============================================================================
// DataSource Form Page
// =============================================================================

export function DataSourceFormPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { id } = useParams();

  if (id === 'new') {
    return <Navigate to="/knowledge/datasources/new" replace />;
  }

  const isEditing = !!id;

  // State
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [providerType, setProviderType] = useState<ProviderType>('git');
  const [providerConfig, setProviderConfig] = useState<Record<string, unknown>>({});
  const [credential, setCredential] = useState<Record<string, unknown>>({});
  const [filterConfig, setFilterConfig] = useState<FilterConfig>(DEFAULT_FILTER_CONFIG);
  const [syncConfig, setSyncConfig] = useState<SyncConfig>(DEFAULT_SYNC_CONFIG);
  const [bindCollectionId, setBindCollectionId] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [oauthCredentialToken, setOauthCredentialToken] = useState<string | null>(null);

  // Queries
  const { data: existing, isLoading: loadingExisting } = useDataSource(id ?? '');
  const { data: collectionsData } = useCollections({ page_size: 100 });
  const collections = collectionsData?.data ?? [];

  // Resolve OAuth provider for the current datasource provider type
  const oauthProvider = PROVIDER_OAUTH_MAP[providerType];

  // Mutations
  const createDS = useCreateDataSource();
  const updateDS = useUpdateDataSource();
  const bindDS = useBindDataSource();

  // Populate form for edit
  useEffect(() => {
    if (existing) {
      setName(existing.name);
      setDescription(existing.description || '');
      setProviderType(existing.provider_type as ProviderType);
      setProviderConfig(existing.provider_config);
      setFilterConfig(existing.filter_config as unknown as FilterConfig);
      setSyncConfig(existing.sync_config as unknown as SyncConfig);
      setBindCollectionId(existing.bound_collection_id || '');
    }
  }, [existing]);

  // Reset provider config when type changes (only in create mode)
  useEffect(() => {
    if (!isEditing) {
      setProviderConfig({});
      setCredential({});
      setOauthCredentialToken(null);
    }
  }, [providerType, isEditing]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error(t('datasources.form.nameRequired'));
      return;
    }

    setSubmitting(true);
    try {
      if (isEditing && id) {
        const hasCredential = Object.values(credential).some((v) => v);
        await updateDS.mutateAsync({
          id,
          name,
          description: description || undefined,
          provider_config: providerConfig,
          ...(hasCredential ? { credential } : {}),
          filter_config: filterConfig,
          sync_config: syncConfig,
        });
        toast.success(t('datasources.updateSuccess'));
      } else {
        const hasCredential = Object.values(credential).some((v) => v);
        const created = await createDS.mutateAsync({
          name,
          description: description || undefined,
          provider_type: providerType,
          provider_config: providerConfig,
          ...(oauthCredentialToken
            ? { oauth_credential_token: oauthCredentialToken }
            : hasCredential
              ? { credential }
              : {}),
          filter_config: filterConfig,
          sync_config: syncConfig,
        });

        // Bind to collection if selected
        if (bindCollectionId) {
          await bindDS.mutateAsync({ id: created.id, collection_id: bindCollectionId });
        }

        toast.success(t('datasources.createSuccess'));
      }
      navigate('/knowledge/datasources');
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('common.error');
      toast.error(isEditing ? t('datasources.updateFailed') : t('datasources.createFailed'), {
        description: msg,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const ProviderForm = PROVIDER_FORMS[providerType];

  if (isEditing && loadingExisting) {
    return (
      <div className="p-6">
        <Card className="p-8 text-center">
          <Icon name="loader" size={24} className="animate-spin mx-auto mb-2" />
          <p className="text-gray-500">{t('common.loading')}</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate('/knowledge/datasources')}>
          <Icon name="arrow-left" size={20} />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {isEditing ? t('datasources.editTitle') : t('datasources.createTitle')}
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            {isEditing ? t('datasources.editSubtitle') : t('datasources.createSubtitle')}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Info */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold mb-4">{t('datasources.form.basicInfo')}</h2>
          <div className="space-y-4">
            <div>
              <Label htmlFor="name">{t('datasources.form.name')}</Label>
              <Input
                id="name"
                placeholder={t('datasources.form.namePlaceholder')}
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
            <div>
              <Label htmlFor="description">{t('datasources.form.description')}</Label>
              <Textarea
                id="description"
                placeholder={t('datasources.form.descriptionPlaceholder')}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
              />
            </div>
            {!isEditing && (
              <div>
                <Label>{t('datasources.form.providerType')}</Label>
                <Select
                  value={providerType}
                  onValueChange={(v) => setProviderType(v as ProviderType)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(['git', 's3', 'sharepoint'] as ProviderType[]).map((pt) => (
                      <SelectItem key={pt} value={pt}>
                        {PROVIDER_LABELS[pt]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </Card>

        {/* Provider Config */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold mb-4">
            {t('datasources.form.providerConfig')} — {PROVIDER_LABELS[providerType]}
          </h2>
          {ProviderForm && (
            <ProviderForm
              config={providerConfig}
              onChange={setProviderConfig}
              credential={credential}
              onCredentialChange={setCredential}
              t={t}
              oauthProvider={oauthProvider}
              oauthConnected={!!oauthCredentialToken}
              onOAuthConnected={setOauthCredentialToken}
            />
          )}
        </Card>

        {/* Filter Config */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold mb-4">{t('datasources.form.filterConfig')}</h2>
          <div className="space-y-4">
            <div>
              <Label>{t('datasources.form.extensions')}</Label>
              <Input
                placeholder=".pdf, .docx, .md, .txt"
                value={filterConfig.include_extensions.join(', ')}
                onChange={(e) =>
                  setFilterConfig({
                    ...filterConfig,
                    include_extensions: e.target.value
                      .split(',')
                      .map((s) => s.trim())
                      .filter(Boolean),
                  })
                }
              />
              <p className="text-xs text-gray-500 mt-1">{t('datasources.form.extensionsHint')}</p>
            </div>
            <div>
              <Label>{t('datasources.form.excludePatterns')}</Label>
              <Input
                placeholder="**/node_modules/**, **/dist/**"
                value={filterConfig.exclude_patterns.join(', ')}
                onChange={(e) =>
                  setFilterConfig({
                    ...filterConfig,
                    exclude_patterns: e.target.value
                      .split(',')
                      .map((s) => s.trim())
                      .filter(Boolean),
                  })
                }
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('datasources.form.maxFileSize')}</Label>
                <Input
                  type="number"
                  value={Math.round(filterConfig.max_file_size_bytes / 1048576)}
                  onChange={(e) =>
                    setFilterConfig({
                      ...filterConfig,
                      max_file_size_bytes: Number(e.target.value) * 1048576,
                    })
                  }
                  min={1}
                  max={500}
                />
                <p className="text-xs text-gray-500 mt-1">MB</p>
              </div>
              <div>
                <Label>{t('datasources.form.maxDepth')}</Label>
                <Input
                  type="number"
                  value={filterConfig.max_depth}
                  onChange={(e) =>
                    setFilterConfig({ ...filterConfig, max_depth: Number(e.target.value) })
                  }
                  min={1}
                  max={20}
                />
              </div>
            </div>
          </div>
        </Card>

        {/* Sync Config */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold mb-4">{t('datasources.form.syncConfig')}</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('datasources.form.syncInterval')}</Label>
              <Input
                type="number"
                value={syncConfig.interval_minutes}
                onChange={(e) =>
                  setSyncConfig({ ...syncConfig, interval_minutes: Number(e.target.value) })
                }
                min={15}
                max={1440}
              />
              <p className="text-xs text-gray-500 mt-1">{t('datasources.form.syncIntervalHint')}</p>
            </div>
            <div>
              <Label>{t('datasources.form.maxRetries')}</Label>
              <Input
                type="number"
                value={syncConfig.max_retries}
                onChange={(e) =>
                  setSyncConfig({ ...syncConfig, max_retries: Number(e.target.value) })
                }
                min={0}
                max={10}
              />
            </div>
          </div>
        </Card>

        {/* Collection Binding (create only) */}
        {!isEditing && (
          <Card className="p-6">
            <h2 className="text-lg font-semibold mb-4">{t('datasources.form.bindCollection')}</h2>
            <p className="text-sm text-gray-500 mb-4">{t('datasources.form.bindCollectionHint')}</p>
            <Select
              value={bindCollectionId || 'none'}
              onValueChange={(v) => setBindCollectionId(v === 'none' ? '' : v)}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('datasources.form.selectCollection')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{t('datasources.form.noCollection')}</SelectItem>
                {collections.map((col) => (
                  <SelectItem key={col.id} value={col.id}>
                    {col.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Card>
        )}

        {/* Submit */}
        <div className="flex items-center gap-3 justify-end">
          <Button variant="outline" type="button" onClick={() => navigate('/knowledge/datasources')}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={submitting}>
            {submitting ? (
              <>
                <Icon name="loader" size={16} className="animate-spin mr-2" />
                {t('common.saving')}
              </>
            ) : isEditing ? (
              t('common.save')
            ) : (
              t('datasources.form.create')
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
